import { useEffect, useRef } from "react";

import { QueryFunction, keepPreviousData, useQuery } from "@tanstack/react-query";

import { useAuthentication } from "@/lib/contexts";
import { useMaintenanceMode } from "@/lib/contexts";
import { CustomError } from "../../errors";

interface QueryWrapperProps<TData> {
  queryKey: string[];
  staleTime?: number;
  gcTime?: number;
  placeholderData?: boolean;
  refetchInterval?: number | false;
  enabled?: boolean;
  onError?: (error: Error) => void;
  queryFn: QueryFunction<TData>;
}

export function useQueryWrapper<TData>({
  queryKey,
  staleTime,
  gcTime,
  placeholderData,
  refetchInterval,
  enabled,
  queryFn,
  onError,
}: QueryWrapperProps<TData>) {
  const { removeUser } = useAuthentication();
  const { setMaintenanceMode, isMaintenanceMode } = useMaintenanceMode();

  // Store onError in a ref so the useEffect below doesn't depend on it.
  // Without this, any unstable callback (e.g. inline arrow functions) causes
  // the effect to re-run on every render while an error is present → infinite loop.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const { isPending, isLoading, isFetching, data, isError, error, isPlaceholderData, refetch } =
    useQuery<TData>({
      queryFn,
      queryKey,
      staleTime,
      gcTime,
      refetchInterval,
      enabled,
      ...(placeholderData ? { placeholderData: keepPreviousData } : {}),
    });

  useEffect(() => {
    if (!error) {
      // If error is cleared, assume we're back online
      isMaintenanceMode && setMaintenanceMode(false);
      return;
    }

    if (error instanceof CustomError) {
      if (error.statusCode === 503) {
        setMaintenanceMode(true);
        return;
      }

      if (error.statusCode === 401 && error.errorCode !== "GUESTY_UNAUTHORIZED") {
        removeUser();
        return;
      }
    }

    onErrorRef.current?.(error);
  }, [error, removeUser, setMaintenanceMode]);

  return {
    isPending,
    isLoading,
    isFetching,
    isPlaceholderData,
    data,
    isError,
    error,
    refetch,
  };
}
